/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import React, { useCallback, useEffect, useState } from 'react';
import { BaseStepCompProps } from '../types';
import { SignUpForm } from '../partials/SignUpForm';
import { BlockingLoader } from '@g17eco/atoms/loader';
import G17Client from '../../../services/G17Client';
import { authWithCredentials } from '../../../services/OktaClient';
import { useAppSelector } from '../../../reducers';
import { getCurrentUser, isStaff } from '../../../selectors/user';
import { Button } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../../constants/routes';
import { generateUrl } from '../../../routes/util';
import { handleRouteError } from '../../../logger';
import { BasicAlert } from '@g17eco/molecules/alert';

type SignupProps = Pick<BaseStepCompProps, 'updateForm' | 'formData' | 'handleNext' | 'handlePrevious' | 'appConfig'>;

export const Signup = (props: SignupProps) => {
  const { formData, appConfig, handleNext } = props;

  const currentUser = useAppSelector(getCurrentUser);
  const isUserStaff = useAppSelector(isStaff);
  const history = useHistory();

  const onboardingPath = appConfig.onboardingPath;

  const [isSubmitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleCreateUser = useCallback(async () => {
    if (!onboardingPath) {
      return;
    }
    setErrorMessage('')
    setSubmitting(true)
    try {
      await G17Client.createUser(formData, onboardingPath);
      const { email: username, password } = formData;
      await authWithCredentials({ username, password });
      setSubmitting(false);
      handleNext();
    } catch (e) {
      setSubmitting(false)
      setErrorMessage(e.message)
      handleRouteError(e, { onboardingPath })
    }
  }, [formData, onboardingPath, handleNext, setSubmitting, setErrorMessage]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }
    const hasInitiatives = currentUser.permissions.length > 0;
    if (!currentUser.active || !hasInitiatives || isUserStaff) {
      handleNext();
      return;
    }
    // Let staff create companies with the same user
    history.push(generateUrl(ROUTES.COMPANY_TRACKER));
  }, [currentUser, handleNext, history, isUserStaff]);

  if (currentUser) {
    const handleGoToCompanyTracker = () => {
      history.push(generateUrl(ROUTES.COMPANY_TRACKER));
    };
    return (
      <div className='mx-5 px-5'>
        <h4>You are currently logged in as <strong>{currentUser.firstName} {currentUser.surname}</strong>.</h4>
        <p>Only one account is allowed per user.</p>
        <div className='mt-5 text-right'>
          <Button color='primary' className='ml-2' onClick={handleGoToCompanyTracker}>
            Go to Company Tracker
          </Button>
        </div>
      </div>
    );
  }

  const text = getText(onboardingPath);

  return (
    <div>
      <BasicAlert type='danger'>{errorMessage}</BasicAlert>
      <div className='d-lg-flex justify-content-between onboarding-form'>
        {isSubmitting ? <BlockingLoader /> : null}
        <aside className='title-container'>
          <div className='h4 text-strong'>
            {text.title}
          </div>
          <p className='text-ThemeTextLight'>{text.subtitle}</p>
          <div className='mt-4 text-ThemeTextMedium'>
            {text.bullets.map((bullet, i) => (
              <div key={i} className='mt-3 d-flex align-items-center'>
                <div><i className='fa fa-check-circle fa-3x text-ThemeAccentLight me-3' /></div>
                <div>{bullet}</div>
              </div>
            ))}
          </div>
        </aside>

        <SignUpForm {...props} handleCreateUser={handleCreateUser} />
      </div>
    </div>
  );
}

interface Texts {
  title: string | JSX.Element;
  subtitle: string;
  bullets: string[];
}

function getText(onboardingPath: string): Texts {
  switch(onboardingPath) {
    case 'materiality-tracker':
      return {
        title: <>Welcome to Materiality Tracker!<br />Let's get your account set up</>,
        subtitle: 'This only takes a minute.',
        bullets: [
          'The most advanced self-service materiality assessment tool',
          'Perform your own materiality assessments without auditors',
          'Invite team members to contribute information',
          'Financial and Double materiality assessments with minimal effort'
        ]
      }
    default:
      return {
        title: <>Welcome!<br />Let's get your account set up</>,
        subtitle: 'This only takes a minute.',
        bullets: [
          'Manage sustainability info in one place',
          'Invite team members to contribute information',
          'Share reports with potential investors and stakeholders'
        ]
      }
  }
}
